import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useFormik } from "formik";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import {
  getCustomerList,
  addLoanApplication,
  updateLoanApplication,
  getLoanApplicationStatusList,
  getTypeLoanList,
  getAmortizationTypesList,
  getWalletListNotVirtual,
} from "helpers/fakebackend_helper";

type Props = {
  onSuccess?: (id: string, isEdit: boolean) => void;
  isEdit: boolean;
};
const LoanApplicationForm = forwardRef(({ onSuccess }: Props, ref) => {

  const FlatpickrComponent = Flatpickr as unknown as React.ComponentType<any>;

  const [loanApplicationStateOptions, setLoanApplicationStateOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [typeLoanOptions, setTypeloanOptions] = useState<
    { value: string; label: string; interestRate: string ; durationPeriod: string }[]
  >([]);

  const [customerOptions, setCustomerOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [amortizationTypesOptions, setAmortizationTypesOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [walletsOptions, setWalletsOptions] = useState<
    { value: string; label: string, balance: string }[]
  >([]);

  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [loanApplicationId, setLoanApplicationId] = useState<string | null>(null);

  const { showLoader, hideLoader } = useLoader();

  const isApproved = () => {
    const currentLoanApplicationStateId = validation.values.loanApplicationStatus;
    const selectedOption = loanApplicationStateOptions.find(
      (opt) => opt.value === currentLoanApplicationStateId
    );

    return selectedOption?.label?.toUpperCase() === "APROBADO";
  };

  const linkifyEntity = (entityName: string, id: number) => {
    return `/${entityName}s/${id}`;
  };

  // Fetch loan application status options
  useEffect(() => {
    const fetchLoanApplciationStatus= async () => {
      try {
        const response = await getLoanApplicationStatusList();
        const embedded = response.data?._embedded?.loanApplicationStatuses;
        const options = embedded
          ? Object.values(embedded).map((loanApplicationState: any) => ({
              value: loanApplicationState.loanApplicationStatusId,
              label: loanApplicationState.name,
            }))
          : [];
          setLoanApplicationStateOptions(options);
      } catch (error) {
        toast.error("Error fetching loanApplicationStates: " + error, { autoClose: 2000 });
      }
    };
    fetchLoanApplciationStatus();
  }, []);

  // Fetch type loan options
  useEffect(() => {
    const fetchTypeLoans= async () => {
      try {
        const response = await getTypeLoanList();
        const embedded = response.data?._embedded?.typeLoans;
        const options = embedded
          ? Object.values(embedded).map((typeLoan: any) => ({
              value: typeLoan.typeLoanId,
              label: typeLoan.name + " - " + typeLoan.interestRate + "%" + " - " + typeLoan.durationPeriod,
              interestRate: typeLoan.interestRate,
              durationPeriod: typeLoan.durationPeriod,
            }))
          : [];
          setTypeloanOptions(options);
      } catch (error) {
        toast.error("Error fetching typeLoans: " + error, { autoClose: 2000 });
      }
    };
    fetchTypeLoans();
  }, []);

  // Fetch customers options
  useEffect(() => {
    const fetchCustomers= async () => {
      try {
        const params = {
          page: 0,
          size: 1000,
          sort: "loanApplicationId,desc"
        };

        const response = await getCustomerList(params);
        const embedded = response.data?._embedded?.customers;
        const options = embedded
          ? Object.values(embedded).map((customer: any) => ({
              value: customer.customerId,
              label: customer.dpi + " - " +customer.fullName,
            }))
          : [];
          setCustomerOptions(options);
      } catch (error) {
        toast.error("Error fetching customers: " + error, { autoClose: 2000 });
      }
    };
    fetchCustomers();
  }, []);

  // Fetch amortization types options
  useEffect(() => {
    const fetchAmortizationTypes= async () => {
      try {
        const response = await getAmortizationTypesList();
        const embedded = response.data?._embedded?.amortizationTypes;
        const options = embedded
          ? Object.values(embedded).map((amortizationType: any) => ({
              value: amortizationType.amortizationTypeId,
              label: amortizationType.name
            }))
          : [];
          setAmortizationTypesOptions(options);
      } catch (error) {
        toast.error("Error fetching amortizationTypes: " + error, { autoClose: 2000 });
      }
    };
    fetchAmortizationTypes();
  }, []);

  // Fetch wallets options
  useEffect(() => {
    const fetchWallets= async () => {
      try {
        let isVirtualWallet = false;
        const response = await getWalletListNotVirtual(isVirtualWallet);
        const embedded = response.data?._embedded?.wallets;
        const options = embedded
          ? Object.values(embedded).map((wallet: any) => ({
              value: wallet.walletId,
              label: wallet.name + " - Saldo: Q" + wallet.balance,
              balance: wallet.balance,
            }))
          : [];
          setWalletsOptions(options);
      } catch (error) {
        toast.error("Error fetching wallets: " + error, { autoClose: 2000 });
      }
    };
    fetchWallets();
  }, []);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      validation.handleSubmit();
    },
    resetForm: () => {
      validation.resetForm();
      setIsEdit(false);
      setLoanApplicationId(null);
    },
    setFormData: (loanApplication: any) => {
      setIsEdit(true);
      setLoanApplicationId(loanApplication.loanApplicationId);
      validation.setValues({
        loanNumber: loanApplication.loanNumber || "",
        loanDuration: loanApplication.loanDuration || "",
        durationPeriod: loanApplication.durationPeriod || "",
        wallet: loanApplication.wallet?.walletId || "",
        interestRate: loanApplication.typeLoan.interestRate || 0,
        amortizationType: loanApplication.amortizationType?.amortizationTypeId || "",
        loanApplicationStatus: loanApplication.loanApplicationStatus?.loanApplicationStatusId || "",
        approvedAmount: loanApplication.approvedAmount || 0,
        amountRequested: loanApplication.amountRequested || 0,
        typeLoan: loanApplication.typeLoan?.typeLoanId || "",
        customer: loanApplication.customer?.customerId || "",
        zeroQuota : loanApplication.zeroQuota || false,
        loanReleasedDate: moment(loanApplication.loanReleasedDate).format('YYYY-MM-DD') || "",
        comments: loanApplication.comments || "",
      });
    },
  }));

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      loanDuration: "",
      durationPeriod: "",
      loanNumber: "",
      wallet: "",
      amortizationType: "",
      loanApplicationStatus: "",
      interestRate: 0,
      approvedAmount: 0,
      loanReleasedDate: "",
      amountRequested: 0,
      typeLoan: "",
      zeroQuota: false,
      customer: "",
      comments: "",
    },
    validationSchema: Yup.object({
      loanDuration: Yup.string().required("Duración del préstamo es requerido"),
      durationPeriod: Yup.string().required("Periodo de duración es requerido"),
      wallet: Yup.string().required("Desde esta cuenta es requerido"),
      amortizationType: Yup.string().required("Tipo de amortización es requerido"),
      loanApplicationStatus: Yup.string().required("Estado es requerido"),
      typeLoan: Yup.string().required("Tipo de préstamo es requerido"),
      zeroQuota: Yup.boolean(),
      customer: Yup.string().required("Cliente es requerido"),
      loanReleasedDate: Yup.string().nullable(),
    }),
    validate: (values) => {
      const errors: Record<string, string> = {};

      const selectedState = loanApplicationStateOptions.find(
        (opt) => opt.value === values.loanApplicationStatus
      );

      const isApproved = selectedState?.label?.toUpperCase() === "APROBADO";

      if (isApproved && !values.loanReleasedDate) {
        errors.loanReleasedDate = "Fecha de aprobación es requerida";
      }

      return errors;
    },
    onSubmit: async (values) => {
      const payload = {
        loanApplicationStatus: linkifyEntity("loanApplicationStatu", parseInt(values.loanApplicationStatus)),
        typeLoan: linkifyEntity("typeLoan", parseInt(values.typeLoan)),
        customer: linkifyEntity("customer", parseInt(values.customer)),
        wallet: linkifyEntity("wallet", parseInt(values.wallet)),
        amortizationType: linkifyEntity("amortizationType", parseInt(values.amortizationType)),
        loanDuration: values.loanDuration,
        durationPeriod: values.durationPeriod,
        approvedAmount: values.amountRequested,
        zeroQuota: values.zeroQuota,
        amountRequested: values.amountRequested,
        loanReleasedDate: values.loanReleasedDate,
        comments: values.comments,
      };

      showLoader();
      try {
        if (isEdit && loanApplicationId) {
          await updateLoanApplication(loanApplicationId, payload);
          toast.success("Solicitud actualizada con exito!", { autoClose: 2000 });
          onSuccess?.(loanApplicationId, isEdit);
        } else {
          const response = await addLoanApplication(payload);
          toast.success("Solicitud creada con exito!", { autoClose: 2000 });
          const newId = response.data.loanApplicationId;
          onSuccess?.(newId, isEdit);
        }
      } catch (error) {
        toast.error("Error saving Loan Application: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    },
  });

  return (
    <React.Fragment>
      <form onSubmit={validation.handleSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

          {/* Numero del prestamo*/}
          <div>
            <label
              htmlFor="loanNumber"
              className="inline-block mb-2 text-base font-medium"
            >
              Número de prestamo
            </label>
            <input
              type="text"
              name="loanNumber"
              disabled
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              value={validation.values.loanNumber}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.loanNumber && validation.errors.loanNumber ? (
              <div className="text-red-500">{validation.errors.loanNumber}</div>
            ) : null}
          </div>

          {/* Estado de la Solicitud*/}
          <div>
            <label
              htmlFor="loanApplicationState"
              className="inline-block mb-2 text-base font-medium"
            >
              Estado<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              data-choices
              name="loanApplicationState"
              options={loanApplicationStateOptions}
              value={loanApplicationStateOptions.find(
                (option) => option.value === validation.values.loanApplicationStatus
              )}
              onChange={(selectedOption: any) => {
                validation.setFieldValue("loanApplicationStatus", selectedOption.value);
              }}
              onBlur={validation.handleBlur}
            />
            {validation.touched.loanApplicationStatus &&
            validation.errors.loanApplicationStatus ? (
              <div className="text-red-500">
                {validation.errors.loanApplicationStatus}
              </div>
            ) : null}
          </div>

          {isApproved() && (
            <>
              {/* Fecha de Aprobacion*/}
              <div>
                <label
                  htmlFor="loanReleasedDate"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Fecha de Aprobación
                </label>
                <FlatpickrComponent
                  id="loanReleasedDate"
                  name="loanReleasedDate"
                  placeholder="Ingrese fecha"
                  options={{
                    altInput: true,
                    altFormat: "d M, Y",
                    dateFormat: "Y-m-d"
                  }}
                  value={validation.values.loanReleasedDate || ""}
                  onChange={(date: any) =>
                    validation.setFieldValue("loanReleasedDate", moment(date[0]).format("YYYY-MM-DD"))
                  }
                  className="form-input w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
                />
                {validation.touched.loanReleasedDate && validation.errors.loanReleasedDate && (
                  <p className="text-sm text-red-500 mt-1">{validation.errors.loanReleasedDate}</p>
                )}
              </div>
            </>
          )}

          {/* Tipo de Prestamo */}
          <div>
            <label
              htmlFor="typeLoan"
              className="inline-block mb-2 text-base font-medium"
            >
              Tipo de Prestamo<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              data-choices
              name="typeLoan"
              options={typeLoanOptions}
              value={typeLoanOptions.find(
                (option) => option.value === validation.values.typeLoan
              )}
              onChange={(selectedOption: any) => {
                validation.setFieldValue("typeLoan", selectedOption.value);
                validation.setFieldValue("durationPeriod", selectedOption.durationPeriod);
                validation.setFieldValue("interestRate", selectedOption.interestRate);
              }}
              onBlur={validation.handleBlur}
            />
            {validation.touched.typeLoan &&
            validation.errors.typeLoan ? (
              <div className="text-red-500">
                {validation.errors.typeLoan}
              </div>
            ) : null}
          </div>

          {/* Cliente*/}
          <div>
            <label
              htmlFor="customer"
              className="inline-block mb-2 text-base font-medium"
            >
              Cliente<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              data-choices
              name="customer"
              options={customerOptions}
              value={customerOptions.find(
                (option) => option.value === validation.values.customer
              )}
              onChange={(selectedOption: any) => {
                validation.setFieldValue("customer", selectedOption.value);
              }}
              onBlur={validation.handleBlur}
            />
            {validation.touched.customer &&
            validation.errors.customer ? (
              <div className="text-red-500">
                {validation.errors.customer}
              </div>
            ) : null}
          </div>

          {/* Tipo de Amortización */}
          <div>
            <label
              htmlFor="typeCustomerSelect"
              className="inline-block mb-2 text-base font-medium"
            >
              Tipo de Amortización<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              data-choices
              name="amortizationType"
              options={amortizationTypesOptions}
              value={(() => {
                const selected = amortizationTypesOptions.find(opt => {
                  return opt.value === validation.values.amortizationType;
                });
                return selected;
              })()}

              onChange={(selected : any) => {
                if (selected) {
                  validation.setFieldValue('amortizationType', selected.value);
                }
              }}
              onBlur={validation.handleBlur}
            />
            {validation.touched.amortizationType &&
            validation.errors.amortizationType ? (
              <div className="text-red-500">
                {validation.errors.amortizationType}
              </div>
            ) : null}
          </div>

          {/* Monto Solicitado*/}
          <div>
            <label
              htmlFor="amountRequested"
              className="inline-block mb-2 text-base font-medium"
            >
              Monto Solicitado <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amountRequested"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese monto solicitado"
              value={validation.values.amountRequested}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.amountRequested && validation.errors.amountRequested ? (
              <div className="text-red-500">{validation.errors.amountRequested}</div>
            ) : null}
          </div>

          {/* Duracion del prestamo*/}
          <div>
            <label
              htmlFor="loanDuration"
              className="inline-block mb-2 text-base font-medium"
            >
              Duración del Préstamo <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="loanDuration"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese duración del prestamo"
              value={validation.values.loanDuration}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.loanDuration && validation.errors.loanDuration ? (
              <div className="text-red-500">{validation.errors.loanDuration}</div>
            ) : null}
          </div>

          {/* Periodo de duracion del prestamo*/}
          <div>
            <label
              htmlFor="durationPeriod"
              className="inline-block mb-2 text-base font-medium"
            >
              Periodo de Duración <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="durationPeriod"
              disabled
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              value={validation.values.durationPeriod}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.durationPeriod && validation.errors.durationPeriod ? (
              <div className="text-red-500">{validation.errors.durationPeriod}</div>
            ) : null}
          </div>

          {/* Tasa de interes*/}
          <div>
            <label
              htmlFor="interestRate"
              className="inline-block mb-2 text-base font-medium"
            >
              Tasa de Interés <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="interestRate"
              disabled
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              value={validation.values.interestRate}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
          </div>

          {/* Desde esta cuenta*/}
          <div>
            <label
              htmlFor="typeCustomerSelect"
              className="inline-block mb-2 text-base font-medium"
            >
              Desde esta Cuenta<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              data-choices
              name="wallet"
              options={walletsOptions}
              value={walletsOptions.find(opt => opt.value === validation.values.wallet)}
              onChange={(selected) => {
                if (selected) {
                  if(Number(selected.balance ) < Number(validation.values.amountRequested)){
                    toast.error("El saldo de la cuenta es menor al monto solicitado");
                    toast.info("Saldo disponible: " + selected.balance);
                    return;
                  }
                  validation.setFieldValue('wallet', selected.value);
                }
              }}
              onBlur={validation.handleBlur}
            />
            {validation.touched.wallet &&
            validation.errors.wallet ? (
              <div className="text-red-500">
                {validation.errors.wallet}
              </div>
            ) : null}
          </div>

          {/* Comentarios*/}
          <div className="md:col-span-2">
            <label
              htmlFor="comments"
              className="inline-block mb-2 text-base font-medium"
            >
              Comentarios
            </label>
            <textarea
              name="comments"
              className="form-textarea w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-custom-500 dark:focus:ring-custom-800 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              value={validation.values.comments}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
          </div>

          {/* Cuota Cero */}
          <div>
            <label
              htmlFor="zeroQuota"
              className="inline-block mb-2 text-base font-medium"
            >Cuota Cero? </label>
            <input
              type="checkbox"
              name="zeroQuota"
              className="form-checkbox border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              checked={validation.values.zeroQuota}
              onChange={validation.handleChange}
            />
            {validation.touched.zeroQuota && validation.errors.zeroQuota ? (
              <div className="text-red-500">{validation.errors.zeroQuota}</div>
            ) : null}
          </div>

        </div>
      </form>
    </React.Fragment>
  );
});

export default LoanApplicationForm;
